
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';
import { Calendar, ArrowRight, Linkedin, User } from 'lucide-react';

const AuthorPage = () => {
    const { id } = useParams();
    const [author, setAuthor] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let authorData = null;
                let authorIds = [];

                // 1. Try fetching by slug
                // We fetch ALL matches to handle potential duplicate author entries
                const slugQ = query(collection(db, 'authors'), where('slug', '==', id));
                const slugSnap = await getDocs(slugQ);

                if (!slugSnap.empty) {
                    // Use the first author found for profile display
                    const firstDoc = slugSnap.docs[0];
                    authorData = { id: firstDoc.id, ...firstDoc.data() };
                    // Collect ALL matching author IDs to find posts linked to any of them
                    authorIds = slugSnap.docs.map(doc => doc.id);
                } else {
                    // 2. Fallback: Try fetching by Document ID directly
                    const authorRef = doc(db, 'authors', id);
                    const authorSnap = await getDoc(authorRef);
                    if (authorSnap.exists()) {
                        authorData = { id: authorSnap.id, ...authorSnap.data() };
                        authorIds = [authorSnap.id];
                    }
                }

                if (authorData) {
                    setAuthor(authorData);

                    // 3. Fetch Posts
                    // Note: 'in' query supports up to 10 IDs. Safe for this use case.
                    // We remove orderBy/multiple-where to avoid needing complex composite indexes.
                    const postsQ = query(
                        collection(db, 'posts'),
                        where('authorId', 'in', authorIds)
                    );

                    const postsSnap = await getDocs(postsQ);
                    let postsData = postsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                    // 4. Client-side Filter & Sort
                    postsData = postsData
                        .filter(post => post.published === true)
                        .sort((a, b) => {
                            const dateA = a.createdAt?.seconds || 0;
                            const dateB = b.createdAt?.seconds || 0;
                            return dateB - dateA; // Descending
                        });

                    setPosts(postsData);
                } else {
                    setAuthor(null);
                }
            } catch (error) {
                console.error("Error fetching author data: ", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    if (loading) {
        return (
            <div className="pt-32 pb-20 text-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A2CB52] mx-auto"></div>
            </div>
        );
    }

    if (!author) {
        return (
            <div className="pt-32 pb-20 text-center min-h-screen px-4">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Author Not Found</h1>
                <Link to="/blog" className="text-[#A2CB52] font-bold hover:underline">
                    &larr; Back to Blog
                </Link>
            </div>
        );
    }

    return (
        <div className="pt-24 pb-20 min-h-screen bg-gray-50">
            <SEO
                title={`${author.name} | Author Profile`}
                description={`Read articles by ${author.name} on BPO Consultants.`}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <FadeIn>
                    {/* Author Profile Header */}
                    <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm mb-12 text-center md:text-left flex flex-col md:flex-row items-center gap-8">
                        <div className="shrink-0 relative">
                            {author.avatarUrl ? (
                                <img
                                    src={author.avatarUrl}
                                    alt={author.name}
                                    className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-gray-100 shadow-md"
                                />
                            ) : (
                                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 border-4 border-gray-100 shadow-md">
                                    <User size={64} />
                                </div>
                            )}
                        </div>
                        <div className="flex-1">
                            <h1 className="text-4xl font-bold text-[#20222D] mb-2">{author.name}</h1>
                            <p className="text-[#A2CB52] font-semibold text-lg mb-4 uppercase tracking-wide">
                                {author.role}
                            </p>
                            <p className="text-gray-600 text-lg max-w-2xl mb-6 leading-relaxed">
                                {author.bio}
                            </p>
                            {author.linkedin && (
                                <a
                                    href={author.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-gray-500 hover:text-[#0077b5] transition-colors"
                                >
                                    <Linkedin size={24} className="mr-2" /> Connect on LinkedIn
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Author's Posts */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6 border-b border-gray-200 pb-4">
                            Latest Articles by {author.name}
                        </h2>

                        {posts.length === 0 ? (
                            <p className="text-gray-500 italic">No articles published yet.</p>
                        ) : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {posts.map((post) => (
                                    <article key={post.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow flex flex-col h-full">
                                        {post.imageUrl ? (
                                            <img
                                                src={post.imageUrl}
                                                alt={post.title}
                                                className="h-48 w-full object-cover"
                                            />
                                        ) : (
                                            <div className="h-48 w-full bg-gray-200 flex items-center justify-center text-gray-400">
                                                No Image
                                            </div>
                                        )}
                                        <div className="p-6 flex-1 flex flex-col">
                                            <div className="flex items-center text-sm text-gray-500 mb-3 space-x-4">
                                                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide">
                                                    {post.category || 'Article'}
                                                </span>
                                                <span className="flex items-center">
                                                    <Calendar size={14} className="mr-1" />
                                                    {post.createdAt?.seconds ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() : 'Date N/A'}
                                                </span>
                                            </div>
                                            <h2 className="text-xl font-bold text-[#20222D] mb-3 line-clamp-2">
                                                <Link to={`/blog/${post.id}`} className="hover:text-[#A2CB52] transition-colors">
                                                    {post.title}
                                                </Link>
                                            </h2>
                                            <p className="text-gray-600 mb-4 line-clamp-3 text-sm flex-1">
                                                {post.excerpt}
                                            </p>
                                            <Link
                                                to={`/blog/${post.id}`}
                                                className="inline-flex items-center text-[#A2CB52] font-bold hover:underline mt-auto"
                                            >
                                                Read Article <ArrowRight size={16} className="ml-1" />
                                            </Link>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                </FadeIn>
            </div>
        </div>
    );
};

export default AuthorPage;
